import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IRole, PermissionsConfigResponse } from '../types/damTypes';
import { getRoles, deleteRole, getPermissionsConfig } from '../services/roleService';
import RoleFormModal from '../components/RoleFormModal';
// Importera komponenter för tabell, formulär, modal senare

const RoleManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionsConfig, setPermissionsConfig] = useState<PermissionsConfigResponse | null>(null);

  // State för modal/formulär
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<IRole | null>(null);

  const fetchRolesAndConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesResponse, permConfigResponse] = await Promise.all([
        getRoles(),
        getPermissionsConfig()
      ]);

      if (rolesResponse.data) {
        setRoles(rolesResponse.data);
      } else {
        setError(rolesResponse.error || 'Kunde inte hämta roller');
      }

      if (permConfigResponse.data) {
        setPermissionsConfig(permConfigResponse.data);
      } else {
        setError(permConfigResponse.error || 'Kunde inte hämta behörighetskonfiguration');
      }

    } catch (err: any) {
      console.error("Fel vid hämtning av roller eller konfig:", err);
      setError(err.message || 'Ett oväntat fel inträffade');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRolesAndConfig();
  }, []);

  const handleOpenModal = (role?: IRole) => {
    setEditingRole(role || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRole(null);
  };

  const handleSaveRole = () => {
    fetchRolesAndConfig();
  };

  const handleDeleteRole = async (roleId: string) => {
    if (window.confirm('Är du säker på att du vill radera denna roll?')) {
      const response = await deleteRole(roleId);
      if (response.error) {
        alert(`Kunde inte radera roll: ${response.error}`);
      } else {
        alert('Roll raderad!');
        fetchRolesAndConfig();
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Roller</Typography>
        <Button variant="contained" onClick={() => handleOpenModal()}>Skapa Ny Roll</Button>
      </Box>
      
      {roles.length === 0 && !loading ? (
        <Typography>Inga roller har skapats än.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Namn</TableCell>
                <TableCell>Beskrivning</TableCell>
                <TableCell align="center">Systemroll</TableCell>
                <TableCell align="center">Antal Behörigheter</TableCell>
                <TableCell align="right">Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow
                  key={role._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {role.name}
                  </TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell align="center">{role.isSystemRole ? 'Ja' : 'Nej'}</TableCell>
                  <TableCell align="center">{role.permissions.length}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Redigera Roll">
                      <IconButton onClick={() => handleOpenModal(role)} size="small" sx={{ mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!role.isSystemRole && (
                      <Tooltip title="Radera Roll">
                        <IconButton onClick={() => handleDeleteRole(role._id)} size="small" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal för att skapa/redigera roll */}
      {isModalOpen && permissionsConfig && (
        <RoleFormModal 
          open={isModalOpen} 
          onClose={handleCloseModal} 
          role={editingRole} 
          permissionsConfig={permissionsConfig} 
          onSave={handleSaveRole}
        />
      )}
    </Box>
  );
};

export default RoleManagementPage; 